import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/services/apiClient';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import ImageUpload from '@/components/ui/ImageUpload';
import { MUSCLE_LABELS, EQUIPMENT_LABELS, DIFFICULTY_LABELS } from '@/lib/enums';

const mediaBox = {
  width: '100%',
  aspectRatio: '16 / 9',
  borderRadius: 'var(--radius-md)',
  overflow: 'hidden',
  background: 'var(--surf2)',
  border: '1px solid var(--line)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const textBlock = { color: 'var(--mid)', fontSize: 13, whiteSpace: 'pre-wrap', margin: 0 };

/**
 * Detalhe de exercício do catálogo global na biblioteca do personal: visualiza
 * a mídia/textos e permite subir imagem/vídeo próprios (valem só pra ele e
 * seus alunos). Textos não são editáveis aqui.
 */
export default function ExerciseDetail({ exercise, onChanged, onClose }) {
  const { t } = useTranslation();
  const [imageUrl, setImageUrl] = useState(exercise.customImageUrl || '');
  const [videoUrl, setVideoUrl] = useState(exercise.customVideoUrl || '');
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [err, setErr] = useState(null);
  const hadOverride = Boolean(exercise.customImageUrl || exercise.customVideoUrl);

  // Preview: prioriza a mídia em edição; senão a efetiva que veio da API.
  const previewVideo = videoUrl || (!imageUrl && exercise.videoUrl) || '';
  const previewImage = imageUrl || exercise.imageUrl || '';

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      await api.put(`/exercises/${exercise.id}/media`, { imageUrl, videoUrl });
      onChanged?.();
      onClose?.();
    } catch (e) {
      setErr(e.message || t('exercises.mediaError'));
    } finally {
      setSaving(false);
    }
  };

  const removeOverride = async () => {
    setRemoving(true);
    setErr(null);
    try {
      await api.delete(`/exercises/${exercise.id}/media`);
      onChanged?.();
      onClose?.();
    } catch (e) {
      setErr(e.message || t('exercises.mediaError'));
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <div style={mediaBox}>
        {previewVideo ? (
          <video src={previewVideo} controls playsInline style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        ) : previewImage ? (
          <img src={previewImage} alt={exercise.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        ) : (
          <span style={{ color: 'var(--low)' }}>—</span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Badge variant="accent">{MUSCLE_LABELS[exercise.muscleGroup] || exercise.muscleGroup}</Badge>
        {exercise.equipment && <Badge>{EQUIPMENT_LABELS[exercise.equipment] || exercise.equipment}</Badge>}
        {exercise.difficulty && <Badge>{DIFFICULTY_LABELS[exercise.difficulty] || exercise.difficulty}</Badge>}
        {hadOverride && <Badge variant="accent">{t('exercises.customMediaTitle')}</Badge>}
      </div>

      {exercise.description && (
        <div>
          <div className="kicker" style={{ marginBottom: 6 }}>{t('exercises.fieldDescription')}</div>
          <p style={textBlock}>{exercise.description}</p>
        </div>
      )}
      {exercise.instructions && (
        <div>
          <div className="kicker" style={{ marginBottom: 6 }}>{t('exercises.fieldInstructions')}</div>
          <p style={textBlock}>{exercise.instructions}</p>
        </div>
      )}
      {exercise.tips && (
        <div>
          <div className="kicker" style={{ marginBottom: 6 }}>{t('exercises.fieldTips')}</div>
          <p style={textBlock}>{exercise.tips}</p>
        </div>
      )}

      <div style={{ borderTop: '1px solid var(--line)', paddingTop: 'var(--space-4)', display: 'grid', gap: 'var(--space-3)' }}>
        <div>
          <div className="kicker">{t('exercises.customMediaTitle')}</div>
          <p style={{ ...textBlock, marginTop: 4 }}>{t('exercises.customMediaHint')}</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
          <ImageUpload label={t('exercises.customImage')} folder="exercises" value={imageUrl} onChange={setImageUrl} />
          <ImageUpload label={t('exercises.customVideo')} folder="exercises" accept="video/mp4" value={videoUrl} onChange={setVideoUrl} />
        </div>
      </div>

      {err && <span style={{ color: 'var(--danger)', fontSize: 13 }}>{err}</span>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
        {hadOverride && (
          <Button type="button" variant="ghost" loading={removing} onClick={removeOverride}>
            {t('exercises.removeMedia')}
          </Button>
        )}
        <Button type="button" variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
        <Button type="button" loading={saving} disabled={!imageUrl && !videoUrl && !hadOverride} onClick={save}>
          {t('exercises.saveMedia')}
        </Button>
      </div>
    </div>
  );
}
